import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { STOCK_CATEGORIES, formatPrice } from '../../constants/stocks';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

const TIME_RANGES = ['1D', '1W', '1M', '3M', '1Y', 'ALL'];

function StockDetailModal({ stock, priceData, holding, onTrade, onClose, newsData }) {
  const [range, setRange] = useState('1W');
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async (selectedRange) => {
    setLoading(true);
    setError(null);
    try {
      const getStockHistory = httpsCallable(functions, 'getStockHistory');
      const result = await getStockHistory({ ticker: stock.ticker, range: selectedRange });
      setHistoryData(result.data);
    } catch (err) {
      setError('Failed to load chart data');
      console.warn('getStockHistory error:', err.message);
    } finally {
      setLoading(false);
    }
  }, [stock.ticker]);

  useEffect(() => {
    fetchHistory(range);
  }, [range, fetchHistory]);

  const category = STOCK_CATEGORIES[stock.category];
  const price = priceData?.price || 0;
  const changePercent = priceData?.changePercent || 0;
  const isPositive = changePercent >= 0;

  // Use range-specific change if available
  const displayChange = historyData ? historyData.rangeChangePercent : changePercent;
  const displayChangeValue = historyData ? historyData.rangeChange : (priceData?.change24h || 0);
  const isRangePositive = displayChange >= 0;

  // P&L for current holding
  let holdingInfo = null;
  if (holding && holding.shares > 0) {
    const currentValue = holding.shares * price;
    const pnl = currentValue - holding.costBasis;
    const pnlPercent = holding.costBasis > 0 ? ((pnl / holding.costBasis) * 100) : 0;
    holdingInfo = { pnl, pnlPercent, currentValue, shares: holding.shares, avgPrice: holding.avgPrice, costBasis: holding.costBasis };
  }

  // Format chart data
  const chartData = historyData?.points?.map(p => ({
    time: p.t,
    price: p.p,
  })) || [];

  // Custom tooltip
  function CustomTooltip({ active, payload }) {
    if (!active || !payload || !payload[0]) return null;
    const point = payload[0].payload;
    const date = new Date(point.time);
    const fmt = range === '1D'
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: range === 'ALL' || range === '1Y' ? 'numeric' : undefined });
    return (
      <div className="stock-chart-tooltip">
        <div className="stock-chart-tooltip-price">{formatPrice(point.price)}</div>
        <div className="stock-chart-tooltip-time">{fmt}</div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stock-detail-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        {/* Header */}
        <div className="stock-detail-header">
          <div className="stock-detail-title">
            <span className="stock-detail-icon">{stock.icon}</span>
            <div>
              <h2 className="stock-detail-name">{stock.name}</h2>
              <span className="stock-detail-category" style={{ color: category?.color }}>
                {category?.label}
              </span>
            </div>
          </div>
          <div className="stock-detail-price-block">
            <div className="stock-detail-price">{formatPrice(price)}</div>
            <div className={`stock-detail-change ${isRangePositive ? 'positive' : 'negative'}`}>
              {isRangePositive ? '+' : ''}{formatPrice(Math.abs(displayChangeValue))} ({isRangePositive ? '+' : ''}{displayChange.toFixed(2)}%)
            </div>
          </div>
        </div>

        <p className="stock-detail-description">{stock.description}</p>

        {/* Time range selector */}
        <div className="stock-detail-ranges">
          {TIME_RANGES.map(r => (
            <button
              key={r}
              className={`stock-range-btn ${range === r ? 'active' : ''}`}
              onClick={() => setRange(r)}
            >
              {r}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="stock-detail-chart">
          {loading ? (
            <div className="stock-chart-loading">Loading chart...</div>
          ) : error ? (
            <div className="stock-chart-error">{error}</div>
          ) : chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={isRangePositive ? '#4ADE80' : '#EF4444'} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={isRangePositive ? '#4ADE80' : '#EF4444'} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="time"
                  tickFormatter={(t) => {
                    const d = new Date(t);
                    if (range === '1D') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    if (range === '1W') return d.toLocaleDateString([], { weekday: 'short' });
                    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                  }}
                  tick={{ fill: 'var(--text-dim)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={40}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fill: 'var(--text-dim)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                  tickFormatter={(v) => v < 0.01 ? v.toFixed(8) : v.toFixed(1)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={isRangePositive ? '#4ADE80' : '#EF4444'}
                  strokeWidth={2}
                  fill="url(#chartGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: isRangePositive ? '#4ADE80' : '#EF4444' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="stock-chart-empty">No chart data available</div>
          )}
        </div>

        {/* Stats row */}
        {historyData && (
          <div className="stock-detail-stats">
            <div className="stock-stat">
              <span className="stock-stat-label">High</span>
              <span className="stock-stat-value">{formatPrice(historyData.high)}</span>
            </div>
            <div className="stock-stat">
              <span className="stock-stat-label">Low</span>
              <span className="stock-stat-value">{formatPrice(historyData.low)}</span>
            </div>
            <div className="stock-stat">
              <span className="stock-stat-label">Current</span>
              <span className="stock-stat-value">{formatPrice(price)}</span>
            </div>
            <div className="stock-stat">
              <span className="stock-stat-label">24h Change</span>
              <span className={`stock-stat-value ${isPositive ? 'positive' : 'negative'}`}>
                {isPositive ? '+' : ''}{changePercent.toFixed(2)}%
              </span>
            </div>
          </div>
        )}

        {/* Holdings section */}
        {holdingInfo && (
          <div className="stock-detail-holdings">
            <h4>Your Position</h4>
            <div className="stock-detail-holdings-grid">
              <div className="stock-holding-item">
                <span className="stock-holding-label">Shares</span>
                <span className="stock-holding-val">{holdingInfo.shares.toFixed(4)}</span>
              </div>
              <div className="stock-holding-item">
                <span className="stock-holding-label">Avg Price</span>
                <span className="stock-holding-val">{formatPrice(holdingInfo.avgPrice)}</span>
              </div>
              <div className="stock-holding-item">
                <span className="stock-holding-label">Value</span>
                <span className="stock-holding-val">{formatPrice(holdingInfo.currentValue)}</span>
              </div>
              <div className="stock-holding-item">
                <span className="stock-holding-label">P&L</span>
                <span className={`stock-holding-val ${holdingInfo.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {holdingInfo.pnl >= 0 ? '+' : ''}${Math.abs(holdingInfo.pnl).toFixed(2)} ({holdingInfo.pnlPercent.toFixed(1)}%)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* News section */}
        {newsData && newsData.length > 0 && (
          <div className="stock-detail-news">
            <h4>Latest News</h4>
            <div className="stock-news-list">
              {newsData.map((headline, i) => (
                <div key={i} className="stock-news-item">
                  <span className="stock-news-bullet">📰</span>
                  <span className="stock-news-headline">{headline}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trade button */}
        <button className="stock-detail-trade-btn" onClick={() => onTrade(stock)}>
          Trade {stock.name}
        </button>
      </div>
    </div>
  );
}

export default StockDetailModal;
